import React, { useState, type ReactNode } from 'react';
import { isEmpty } from 'lodash';
import FloatingPopover from '../FloatingPopover';

/**
 * The attribute properties shown in the popover. Each is built by the parent
 * (AttributesInspector) via `attributeProperty`, which returns the stored
 * change, the original value, or `undefined` — hence the optional/nullable
 * shape.
 */
interface AttributePopoverProperties {
  description?: string | null;
  implementation?: string | null;
  validation?: string | null;
}

interface AttributePopoverProps {
  /** The attribute properties to display. */
  attribute: AttributePopoverProperties;
  /**
   * Preferred placement of the popover relative to the target. See
   * https://github.com/littlebits/react-popover#code-preferplace-enum-string-null-code
   */
  preferPlace?: string | null;
  /** The trigger element the popover anchors to. */
  children?: ReactNode;
}

/**
 * A popover with information about an attribute.
 *
 * Uses FloatingPopover (a @floating-ui/react drop-in for the retired
 * react-popover, which crashed under React 19 via findDOMNode).
 *
 * Shown when hovering over an attribute in the Inspector. A wrapper div is
 * added around the children. The popover is shown onMouseOver and hidden
 * onMouseOut.
 *
 * Props:
 * - attribute: the attribute properties
 * - preferPlace: see
 *   https://github.com/littlebits/react-popover#code-preferplace-enum-string-null-code
 *
 * Not wrapped in observer(): the class wasn't either. `attribute` is a plain
 * object snapshot built by the parent (AttributesInspector), so there is no MST
 * observable to track here.
 */
export default function AttributePopover({ attribute, preferPlace, children }: AttributePopoverProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Mirrors the class `toggle(toState = null)`.
  const toggle = (toState: boolean | null = null) => {
    setIsOpen((prev) => (toState === null ? !prev : toState));
  };

  const renderDescription = () => {
    const { description } = attribute;
    if (!isEmpty(description)) {
      return (
        <div className="mb-2 last:mb-0">
          <span className="font-sans font-medium tracking-normal">Description:</span>
          <br />
          {description}
        </div>
      );
    }
  };

  const renderValidation = () => {
    const { validation } = attribute;
    if (!isEmpty(validation)) {
      return (
        <div className="mb-2 last:mb-0">
          <span className="font-sans font-medium tracking-normal">Validation rule:</span>
          <br />
          {validation}
        </div>
      );
    }
  };

  const renderImplementation = () => {
    const { implementation } = attribute;
    if (!isEmpty(implementation)) {
      return (
        <div className="mb-2 last:mb-0">
          <span className="font-sans font-medium tracking-normal">Implementation:</span>
          <br />
          {implementation}
        </div>
      );
    }
  };

  const renderBody = () => {
    return (
      <div className="attribute-popover-content font-mono tracking-[-0.5px]">
        {renderDescription()}
        {renderValidation()}
        {renderImplementation()}
      </div>
    );
  };

  return (
    <FloatingPopover preferPlace={preferPlace} isOpen={isOpen} body={renderBody()}>
      <div
        className="attribute-popover-trigger"
        onMouseOver={() => toggle(true)}
        onMouseOut={() => toggle(false)}
        children={children}
      />
    </FloatingPopover>
  );
}
